import { call } from 'redux-saga/effects';
import { CloudActions, CostCenter } from '@portal/portal-core';
import { get } from 'lodash-es';

import { sendAction, type SuperClientConfig, type WrapResult } from '../api';
import { SUPER_ADMIN_LICENSE_ERR } from '../types';
import { individualMatch } from '../utils';

// Minimal shape of the action consumed by `getOrCreateCostCenter`
interface CostCenterAction {
  existingLicensesInfo?: unknown;
  costCenterName?: string;
  costCenterType?: string;
  costCenterID?: string;
  ownerID?: string;
  whitelistSuffixes?: string;
  whitelistIgnore?: boolean;
  countryCode?: string;
  admins?: string[];
  authorizedDevicesOverride?: boolean;
}

// Slightly custom error with property for failing action types
export class LicenseError extends Error {
  type: string;

  actions: unknown[];

  constructor(actions: unknown[] = []) {
    super(SUPER_ADMIN_LICENSE_ERR);
    this.type = SUPER_ADMIN_LICENSE_ERR;
    this.actions = actions;
  }
}

// Shared cost center creation helper -- same for all 3 products
export function* getOrCreateCostCenter(
  config: SuperClientConfig,
  action: CostCenterAction,
): Generator<unknown, { costCenterID: unknown }, unknown> {
  try {
    const existingIndividualCostCenterId = yield individualMatch(
      action.existingLicensesInfo as Parameters<typeof individualMatch>[0],
      action.costCenterName as string,
    );
    // if individual then try to find and add license to existing individual cost center
    if (
      action.costCenterType === CostCenter.COST_CENTER_TYPES.individual &&
      existingIndividualCostCenterId
    ) {
      return {
        costCenterID: existingIndividualCostCenterId,
      };
    }
    // if the action has explicitly provided a cost center the license should be added to, then return that costCenterId
    if (action.costCenterID) {
      return {
        costCenterID: action.costCenterID,
      };
    }
    // else create a new cost center and return that cost center's id
    const createCostCenterAction = CloudActions.adminCreateNewCostCenter(
      action.ownerID,
      action.costCenterName,
      action.whitelistSuffixes,
      action.whitelistIgnore,
      action.costCenterType,
      action.countryCode,
      action.admins,
      // cloud-core types this 8th arg as `requestId?: string`; runtime historically
      // passes the boolean override here, so preserve that value exactly.
      action.authorizedDevicesOverride as unknown as string | undefined,
    );
    const res = (yield call(sendAction, config, createCostCenterAction)) as WrapResult;
    const costCenterID = get(res, 'data.costCenter.id') as unknown;
    if (res.succeeded) {
      return {
        costCenterID,
      };
    }
    // Historically invoked without `new`, which throws at runtime and is caught below.
    // Preserve that exact behavior.
    return (LicenseError as unknown as (actions?: unknown[]) => never)([
      createCostCenterAction.type,
    ]);
  } catch (err) {
    throw new LicenseError([err]);
  }
}

export const pause = (ms = 0): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function* sendActionWithRetries(
  config: SuperClientConfig,
  action: Parameters<typeof sendAction>[1],
  maxAttempts = 2,
  attemptCount = 1,
  reattemptDelay = 6000,
  requestSpacing = 0,
): Generator<unknown, WrapResult, unknown> {
  const res = (yield call(sendAction, config, action)) as WrapResult;
  if (res.succeeded) {
    return res;
  }
  yield call(pause, requestSpacing);
  if (attemptCount < maxAttempts) {
    yield call(pause, reattemptDelay);
    return (yield call(
      sendActionWithRetries,
      config,
      action,
      maxAttempts,
      attemptCount + 1,
    )) as WrapResult;
  }
  throw new Error(`response failed after ${maxAttempts} attempts`);
}

export const ccTypes = {
  institutional: 'institutional',
  individual: 'individual',
};

// Minimal shape of the user consumed by `transformUser`
interface TransformUserInput {
  emails?: Record<string, { verified?: boolean } | undefined>;
  primaryEmail: string;
  firstname?: string;
  lastname?: string;
  id?: string | number;
  phantom?: boolean;
  status?: string;
  country?: string;
  [key: string]: unknown;
}

interface TransformedUser {
  searchString: string;
  verified: boolean;
  isVerified: string;
  isPhantom: string;
  status: string;
  country: string;
  fullname: string;
  [key: string]: unknown;
}

export const transformUser = (user: TransformUserInput): TransformedUser => {
  const verified = !!user.emails?.[user.primaryEmail]?.verified;
  return {
    ...user,
    searchString: `${user.firstname} ${user.lastname} ${user.primaryEmail} ${user.id}`,
    verified,
    isVerified: verified ? 'true' : 'false',
    isPhantom: user.phantom ? 'true' : 'false',
    status: user.status || '',
    country: user.country || '',
    fullname: `${(user.firstname || '').toLowerCase()} ${(user.lastname || '').toLowerCase()}`,
  };
};
